import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Clock, Gift, RotateCw } from "lucide-react";
import api, { getErr } from "../lib/api";
import { NeonButton, NeonCard, SectionTitle, Spinner, Pill } from "../components/Neon";
import { fmtDateTime } from "../lib/format";
import { useDocumentSeo } from "../lib/seo";

// Builds a conic-gradient string from the wheel segments so each slice is evenly sized.
const wheelBackground = (segments) => {
  const n = segments.length || 1;
  const slice = 360 / n;
  const stops = segments.map((s, i) => `${s.color || "#3A3A3A"} ${i * slice}deg ${(i + 1) * slice}deg`);
  return `conic-gradient(from 0deg, ${stops.join(", ")})`;
};

export default function Spin() {
  useDocumentSeo({ title: "Daily Spin & Win | Richi£ Rich Competitions", description: "Spin the daily wheel for discounts and free tickets." });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const [reward, setReward] = useState(null);
  const [rotation, setRotation] = useState(0);
  const rotRef = useRef(0);

  const load = () => {
    setLoading(true);
    api.get("/spin")
      .then(({ data }) => setStatus(data))
      .catch((e) => toast.error(getErr(e, "Could not load the spin wheel")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const segments = status?.segments || [];
  const canSpin = !!status?.enabled && !!status?.can_spin;

  const doSpin = async () => {
    if (!canSpin || spinning) return;
    setSpinning(true);
    setReward(null);
    try {
      const { data } = await api.post("/spin");
      const n = segments.length || 8;
      const slice = 360 / n;
      const idx = typeof data.segment_index === "number" ? data.segment_index : 0;
      // Land the chosen segment's centre under the top pointer, after several full turns.
      const target = 360 * 6 + (360 - (idx * slice + slice / 2));
      const next = rotRef.current + target;
      rotRef.current = next;
      setRotation(next);
      // Reveal the reward after the CSS spin animation settles.
      setTimeout(() => {
        setReward(data.reward || null);
        const msg = data.reward?.message || (data.reward?.label ? `You won ${data.reward.label}!` : "Better luck tomorrow!");
        if (data.reward?.type && data.reward.type !== "none") toast.success(msg); else toast(msg);
        setSpinning(false);
        // Refresh status so the button reflects the used-up daily spin.
        api.get("/spin").then(({ data: s }) => setStatus(s)).catch(() => {});
      }, 4200);
    } catch (e) {
      toast.error(getErr(e, "Spin failed"));
      setSpinning(false);
    }
  };

  if (loading) return <Spinner className="min-h-[60vh]" />;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10" data-testid="spin-page">
      <SectionTitle accent="cyan" sub="One free spin every day. Win instant discounts and free tickets to use at checkout.">
        Daily Spin &amp; Win
      </SectionTitle>

      <div className="mt-8 grid lg:grid-cols-2 gap-8 items-center">
        {/* Wheel */}
        <div className="flex flex-col items-center">
          <div className="relative" style={{ width: 320, height: 320 }}>
            {/* Pointer */}
            <div className="absolute left-1/2 -top-1 z-20 -translate-x-1/2" style={{ width: 0, height: 0, borderLeft: "14px solid transparent", borderRight: "14px solid transparent", borderTop: "22px solid var(--neon-pink)" }} />
            <div
              data-testid="spin-wheel"
              className="absolute inset-0 rounded-full border-4 border-white/15 shadow-[0_0_40px_rgba(0,229,255,.25)]"
              style={{
                background: segments.length ? wheelBackground(segments) : "var(--surface)",
                transform: `rotate(${rotation}deg)`,
                transition: "transform 4s cubic-bezier(0.16, 1, 0.3, 1)",
              }}
            />
            {/* Segment labels */}
            <div className="absolute inset-0" style={{ transform: `rotate(${rotation}deg)`, transition: "transform 4s cubic-bezier(0.16, 1, 0.3, 1)" }}>
              {segments.map((s, i) => {
                const slice = 360 / segments.length;
                const angle = i * slice + slice / 2;
                return (
                  <div key={i} className="absolute left-1/2 top-1/2 origin-left" style={{ transform: `rotate(${angle}deg) translate(36px, -50%)` }}>
                    <span className="block whitespace-nowrap text-[10px] font-display uppercase tracking-wide text-white drop-shadow-[0_1px_2px_rgba(0,0,0,.9)]" style={{ width: 110 }}>{s.label}</span>
                  </div>
                );
              })}
            </div>
            {/* Hub */}
            <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 h-16 w-16 rounded-full bg-black border-2 border-[var(--neon-cyan)] flex items-center justify-center shadow-[0_0_20px_rgba(0,229,255,.4)]">
              <Sparkles className="h-6 w-6 text-[var(--neon-cyan)]" />
            </div>
          </div>

          <NeonButton variant="pink" className="mt-8 w-56" data-testid="spin-button" onClick={doSpin} loading={spinning} disabled={!canSpin}>
            <RotateCw className="h-4 w-4" /> {spinning ? "Spinning…" : canSpin ? "Spin Now" : "Come Back Tomorrow"}
          </NeonButton>
          {!canSpin && status?.next_reset && (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-[var(--text-muted)]" data-testid="spin-next-reset">
              <Clock className="h-3.5 w-3.5" /> Next free spin: {fmtDateTime(status.next_reset)}
            </p>
          )}
          {!status?.enabled && (
            <p className="mt-3 text-xs text-[var(--text-muted)]">The spin wheel is currently unavailable. Please check back soon.</p>
          )}
        </div>

        {/* Reward / info panel */}
        <div>
          {reward ? (
            <NeonCard variant={reward.type && reward.type !== "none" ? "green" : "soft"} innerClassName="p-6" data-testid="spin-reward">
              <Gift className="h-8 w-8 text-[var(--neon-gold)]" />
              <p className="mt-3 font-display uppercase text-3xl text-white glow-white">{reward.label || "No win"}</p>
              <p className="mt-2 text-sm text-[var(--text-muted)]">{reward.message || "Better luck tomorrow — your next free spin is on the way."}</p>
              {reward.discount_pct > 0 && (
                <div className="mt-4"><Pill color="cyan">{Math.round(reward.discount_pct)}% off applied at checkout today</Pill></div>
              )}
            </NeonCard>
          ) : (
            <NeonCard variant="flow" innerClassName="p-6">
              <p className="font-display uppercase text-2xl text-white">How it works</p>
              <ul className="mt-3 space-y-2 text-sm text-[var(--text-muted)]">
                <li>• Spin once per day — completely free.</li>
                <li>• Land on a discount and it's auto-applied to your next purchase that day.</li>
                <li>• Free-ticket wins drop straight into your account.</li>
                <li>• Your spin resets every day at midnight.</li>
              </ul>
              <div className="mt-5 flex flex-wrap gap-2">
                {segments.map((s, i) => (
                  <Pill key={i} color={s.type === "free_ticket" ? "gold" : s.type === "discount" ? "cyan" : "grey"}>{s.label}</Pill>
                ))}
              </div>
            </NeonCard>
          )}
        </div>
      </div>
    </div>
  );
}
